import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import getGridWidthClass from '../../utils/getGridWidthClass';

// Dark mode hover styles
const darkHoverStyles = `
  .dark-hover-menu:hover .dark-hover-text {
    color: black !important;
  }
  .dark-hover-menu:hover .dark-hover-icon {
    filter: none !important;
  }
`;

// Inject styles if not already present
if (!document.getElementById('dark-hover-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'dark-hover-styles';
  styleSheet.textContent = darkHoverStyles;
  document.head.appendChild(styleSheet);
}

import InputText from '../InputText';
import InputDateRange from '../InputDateRange';
import InputMultiSelect from '../InputMultiSelect';
import InputLabelWrapper from '../InputLabelWrapper';
import { useTheme } from '../../contextProviders';
import Input from '../Input/Input';
import { shareIcon, excelIcon, copyIcon, mergeIcon, addToStudyResearch, viewLocalFiles, CDIcon, downloadIcon, extraStorageIconSvg, deleteIcon } from '../../../assets/svgIcons/icon';
import { useNavigate } from 'react-router-dom';
import { servicesManager } from '../../../../app/src/App';


const InputGroup = ({
  inputMeta,
  values,
  onValuesChange,
  sorting,
  onSortingChange,
  isSortingEnabled,
  studiesToSend,
  handleDownloadExcel,
  duplicateStudyHandler,
  setMergeModal,
  handleAddToResearch,
  handleDownloadDicomClick,
  handleCreateISO,
  generateStudyLinkHandler,
  isSelectAll,
  handleSelectAllClick,
  handleMoveToExtraStorage,
  handleDeleteDicomClick
}) => {
  const { isDarkMode } = useTheme();
  const { sortBy, sortDirection } = sorting;

  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const divRef = useRef(null);
  const { uiNotificationService } = servicesManager.services

  const role = localStorage.getItem('role') || sessionStorage.getItem('role');

  const handleShareStudies = () => {
    if (studiesToSend?.length === 0) {
      uiNotificationService.show({
        title: "Warning",
        position: 'topRight',
        type: "error",
        message: 'Please select atleast one studies.'
      })
      return;
    }
    if (!localStorage.getItem('local_pacs_access_token') && !sessionStorage.getItem('local_pacs_access_token')) {
      uiNotificationService.show({
        title: 'Send DICOM',
        position: 'topRight',
        message: 'No PACS is ACTIVE. Please ACTIVATE a PACS and try again.',
        type: 'error',
      });
      return;
    }
    navigate('/sendtoMachine', {
      state: {
        studiesToSend,
        isSendingSeries: false,
      },
    });
  }

  const handleLocalStudyClick = () => {
    navigate('/local');
  }

  const handleFilterLabelClick = name => {
    if (isSortingEnabled) {
      let _sortDirection = 'descending';
      if (sortBy === name) {
        if (sortDirection === 'ascending') {
          _sortDirection = 'descending';
        } else if (sortDirection === 'descending') {
          _sortDirection = 'ascending';
        }
      }

      onSortingChange({
        sortBy: _sortDirection !== 'none' ? name : '',
        sortDirection: _sortDirection,
      });
    }
  };


  const renderInputFieldLabel = ({ name, displayName, inputProps, isSortable, inputType }) => {
    const _isSortable = isSortable && isSortingEnabled;
    const _sortDirection = sortBy !== name ? 'none' : sortDirection;

    const onLabelClick = () => {
      handleFilterLabelClick(name);
    }

    return (
      <InputLabelWrapper
        key={name}
        label={displayName}
        isSortable={_isSortable}
        sortDirection={_sortDirection}
        onLabelClick={onLabelClick}
        className={`${isDarkMode ? "text-black" : "text-white"}`}
      />
    )
  };


  const renderFieldInputComponent = ({ name, displayName, inputProps, isSortable, inputType,placeholder }) => {
    const _isSortable = isSortable && isSortingEnabled;
    const _sortDirection = sortBy !== name ? 'none' : sortDirection;
    const onLabelClick = () => {
      handleFilterLabelClick(name);
    };

    const handleFieldChange = newValue => {
      onValuesChange({
        ...values,
        [name]: newValue,
      });
    };

    const handleDateRangeFieldChange = ({ startDate, endDate }) => {
      onValuesChange({
        ...values,
        [name]: {
          startDate: startDate,
          endDate: endDate,
        },
      });
    };

    const handleDobChange = (event) => {
      onValuesChange({
        ...values,
        [name]: event.target.value
      })
    }

    switch (inputType) {
      case 'Text':
        return (
          <InputText
            id={name}
            key={name}
            onLabelClick={onLabelClick}
            value={values[name]}
            onChange={handleFieldChange}
            placeholder={placeholder}
          />
        );
      case 'Checkbox':
        return (
          <input
            type="checkbox"
            id={name}
            style={{position: 'absolute', top: '80%', left: '96%', transform: 'translate(-96%, -50%)'}}
            key={name}
            onClick={handleSelectAllClick}
            checked={isSelectAll}
          />
        );
      case 'MultiSelect':
        return (
          <InputMultiSelect
            id={name}
            key={name}
            onLabelClick={onLabelClick}
            value={values[name]}
            onChange={handleFieldChange}
            options={inputProps.options}
          />
        );
      case 'DateRange':
        return (
          <InputDateRange
            id={name}
            key={name}
            onLabelClick={onLabelClick}
            value={values[name]}
            onChange={handleDateRangeFieldChange}
          />
        );
      case 'None':
        return (
          <InputLabelWrapper
            key={name}
            onLabelClick={onLabelClick}
            className={isDarkMode ? "text-black" : "text-white"}
          />
        );

      case 'date':
        return (<>
        {/* @ts-ignore */}
          <Input
            id={name}
            className="pb-1.5"
            type="date"
            containerClassName="mr-2 py-0"
            value={values[name]}
            onChange={handleDobChange}
          /></>)
      default:
        break;
    }
  };

  const mergeModalHandler = () => {
    if (studiesToSend.length > 1) {
      setMergeModal(true)
    }
    else {
      uiNotificationService.show({
        title: "Warning",
        position: 'topRight',
        type: "error",
        message: 'Please select Atleast two studies'
      })
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the div and showMenu is true (i.e., modal is open)
      if (divRef.current && !divRef.current.contains(event.target) && showMenu) {
        setShowMenu(false); // Close the modal when clicking outside
      }
    };

    // Add event listener when the modal is shown
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener when the modal is closed
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className=" relative m-auto flex flex-col">
      <div className='flex relative'>
        <div className={classnames(isDarkMode ? 'flex w-full flex-row tableHeaderDark py-4' : 'flex w-full flex-row tableHeader py-4')}>
          {inputMeta.map(inputMeta => {
            return (
              <div
                key={inputMeta.name}
                className={classnames('thead-text margin-left-10', getGridWidthClass(inputMeta.gridCol))}
              >
                {renderInputFieldLabel(inputMeta)}
              </div>
            );
          })}
        </div>
        <div className='absolute right-8 text-[#5b5baa] border-2 border-[#5b5baa] rounded-2xl px-1 p-0 !mt-2 font-bold'>
          <button className='cursor-pointer p-2 text-[13px]' onClick={() => setShowMenu(!showMenu)}>More Options</button>
          {
            showMenu &&
            <div id='ellipse-menu' ref={divRef} className="absolute bg-white" style={{ width: 'auto', minWidth: '190px', right: '10px', zIndex: 3, boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.19)' }}>
             { role !== 'Superadmin' && <div className={`hover:bg-gray-200 flex gap-2 cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleShareStudies}>
                <img src={shareIcon} style={
                  isDarkMode ? { filter: 'invert(1)' } : null} className={isDarkMode ? 'dark-hover-icon' : ''} />
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Send DICOM</p>
              </div> }
              <hr />
              <div className={`hover:bg-gray-200 flex items-center justify-start gap-2 cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleDownloadExcel}>
                <div className='h-5 w-5'>
                  <img src={excelIcon} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Export to Excel</p>
              </div>
              <hr />
              {role !== 'Superadmin' &&  <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={duplicateStudyHandler}>
                <div className='h-5 w-5'>
                  <img src={copyIcon} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Duplicate Study</p>
              </div>}
              <hr />
              {role !== 'Superadmin' && <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={mergeModalHandler}>
                <div className='h-5 w-5'>
                  <img src={mergeIcon} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Merge Study</p>
              </div>}

              <hr />
              <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleCreateISO}>
                <div className='h-5 w-5'>
                  <img src={CDIcon} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Create ISO image</p>
              </div>
              <hr />
              <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={generateStudyLinkHandler}>
                <div className='h-5 w-5'>
                <img src={shareIcon} style={
                  isDarkMode ? { filter: 'invert(1)' } : null} className={isDarkMode ? 'dark-hover-icon' : ''} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Share study link</p>
              </div>
              <hr />
                { role !== 'Superadmin' && <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleAddToResearch}>
                <div className='h-5 w-5'>
                  <img src={addToStudyResearch} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Add to Study Research</p>
              </div> }
              <hr/>
                { role !== 'Superadmin' && <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleMoveToExtraStorage}>
                <div className='h-5 w-5'>
                  <img src={extraStorageIconSvg} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Switch storage</p>
              </div> }
              <hr />
              <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleLocalStudyClick}>
                <div className='h-5 w-5'>
                  <img src={viewLocalFiles} style={
                    isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>View Local Study</p>
              </div>
              <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleDownloadDicomClick}>
                <div className='h-5 w-5'>
                  <img src={downloadIcon} style={
                    !isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Download DICOM Files</p>
              </div>
              <div className={`hover:bg-gray-200 flex gap-2 items-center justify-start cursor-pointer p-2 ${isDarkMode ? 'dark-hover-menu' : ''}`} onClick={handleDeleteDicomClick}>
                <div className='h-5 w-5'>
                  <img src={deleteIcon} style={
                    !isDarkMode ? { filter: 'invert(1)' } : null} className={`object-contain h-[100%] w-[100%] ${isDarkMode ? 'dark-hover-icon' : ''}`} />
                </div>
                <p className={`text-[12px] font-regular ${isDarkMode ? 'dark-hover-text' : ''}`}>Delete DICOM Files</p>
              </div>
            </div>
          }
        </div>
      </div>
      <div className="flex w-full flex-row ohiViewerFields">
        {inputMeta.map(inputMeta => {
          return (
            <div
              key={inputMeta.name}
              className={classnames('ohiItem margin-left-10', getGridWidthClass(inputMeta.gridCol))}
            >
              {renderFieldInputComponent(inputMeta)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

InputGroup.propTypes = {
  inputMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      gridCol: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).isRequired,
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  values: PropTypes.object.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  sorting: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDirection: PropTypes.oneOf(['ascending', 'descending', 'none']),
  }).isRequired,
  onSortingChange: PropTypes.func.isRequired,
  isSortingEnabled: PropTypes.bool.isRequired,
};

export default InputGroup;
